import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import DataTableCard from '../../Components/Table/DataTableCard';

const DivisionList = () => {
  const navigate = useNavigate();
  const { showToast, showConfirm } = useToast();

  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);

  // Form State
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [status, setStatus] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const fetchDivisions = async () => {
    try {
      setLoading(true);
      const result = await api.getAllFirstThierProgram();
      setDivisions(result);
    } catch (error) {
      console.error('Error fetching divisions:', error);
      showToast('Gagal memuat data divisi.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.getEmployees({ pageSize: 100, page: 1 });
      if (response && response.employees) {
        setEmployees(response.employees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    fetchDivisions();
    fetchEmployees();
  }, []);

  const openAddModal = () => {
    setIsEditMode(false);
    setCurrentId(null);
    setName('');
    setDescription('');
    setEmployeeId('');
    setStatus(1);
    setShowModal(true);
  };

  const openEditModal = (div) => {
    setIsEditMode(true);
    setCurrentId(div.id);
    setName(div.name || '');
    setDescription(div.description || '');

    // Find employee ID by name if possible, or we will match employeeId below.
    // In our backend GetFirstTierDetail returns the employeeId as id_emp. Let's load the full detail first.
    loadDivisionDetailForEdit(div.id);
  };

  const loadDivisionDetailForEdit = async (id) => {
    try {
      const detail = await api.getFirstThierProgramById({ id });
      if (detail) {
        setName(detail.name || '');
        setDescription(detail.description || '');
        // detail.responsible is an array like [{ id: 12, fullName: "..." }]
        if (detail.responsible && detail.responsible.length > 0) {
          setEmployeeId(detail.responsible[0].id || '');
        } else {
          setEmployeeId('');
        }
        setStatus(detail.status ? 1 : 0);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching division detail:', error);
      showToast('Gagal memuat detail divisi untuk diedit.', 'danger');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast('Nama divisi harus diisi.', 'danger');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name,
        description,
        employeeId: employeeId ? Number(employeeId) : null,
        status: Number(status),
      };

      if (isEditMode) {
        await api.updateFirstTierProgram({ id: currentId, ...payload });
        showToast('Divisi berhasil diperbarui!', 'success');
      } else {
        await api.addFirstTierProgram(payload);
        showToast('Divisi baru berhasil ditambahkan!', 'success');
      }

      setShowModal(false);
      fetchDivisions();
    } catch (error) {
      console.error('Error saving division:', error);
      showToast(`Gagal menyimpan divisi: ${error.message || error}`, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    const confirmed = await showConfirm(
      `Apakah Anda yakin ingin menghapus divisi "${name}" secara permanen?`,
      'Hapus Divisi',
      'danger'
    );
    if (!confirmed) return;

    try {
      await api.deleteFirstTierProgram({ id });
      showToast('Divisi berhasil dihapus!', 'success');
      fetchDivisions();
    } catch (error) {
      console.error('Error deleting division:', error);
      showToast(`Gagal menghapus divisi: ${error.message || error}`, 'danger');
    }
  };

  return (
    <div className="container-fluid p-0">
      <DataTableCard
        icon="bi-grid-3x3-gap-fill"
        title="Manajemen Divisi (First Tier)"
        subtitle="Kelola divisi, penanggung jawab, dan program di bawahnya."
        actions={
          <button
            onClick={openAddModal}
            className="btn btn-primary rounded-pill px-3"
          >
            <i className="bi bi-plus-circle me-2"></i>Tambah Divisi
          </button>
        }
      >
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light text-muted small text-uppercase fw-bold">
              <tr>
                <th className="ps-4 py-3 border-0">Divisi</th>
                <th className="border-0 py-3">Deskripsi</th>
                <th className="border-0 py-3">Manajer</th>
                <th className="border-0 py-3">Program</th>
                <th className="border-0 py-3 text-center">Status</th>
                <th className="border-0 py-3 text-end pe-4">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="placeholder-glow">
                    <td className="ps-4 py-3"><span className="placeholder col-8 rounded"></span></td>
                    <td className="py-3"><span className="placeholder col-10 rounded"></span></td>
                    <td className="py-3"><span className="placeholder col-6 rounded"></span></td>
                    <td className="py-3"><span className="placeholder col-6 rounded"></span></td>
                    <td className="py-3 text-center"><span className="placeholder col-4 rounded"></span></td>
                    <td className="text-end pe-4 py-3"><span className="placeholder col-4 rounded"></span></td>
                  </tr>
                ))
              ) : divisions.length > 0 ? (
                divisions.map((div) => (
                  <tr
                    key={div.id}
                    className="cursor-pointer transition-all"
                    onClick={() => navigate(`/division/${div.id}`)}
                  >
                    <td className="ps-4 py-3">
                      <div className="fw-bold text-dark">{div.name}</div>
                    </td>
                    <td className="py-3">
                      <span
                        className="small text-secondary text-truncate d-inline-block"
                        style={{ maxWidth: '220px' }}
                      >
                        {div.description || <em>Tidak ada deskripsi.</em>}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className="small text-muted">
                        <i className="bi bi-person-fill text-primary me-1"></i>
                        {div.responsible || 'Belum ditentukan'}
                      </span>
                    </td>
                    <td className="py-3">
                      {div.programs && div.programs.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1">
                          {div.programs.map((p, index) => (
                            <span
                              key={index}
                              className="badge bg-secondary bg-opacity-10 text-secondary rounded-pill px-3 py-2"
                            >
                              {p}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted extra-small">-</span>
                      )}
                    </td>
                    <td className="py-3 text-center">
                      <span
                        className={`badge bg-${div.status ? 'success' : 'secondary'} bg-opacity-10 text-${div.status ? 'success' : 'secondary'} rounded-pill px-3 py-2`}
                      >
                        {div.status ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td className="py-3 pe-4" onClick={(e) => e.stopPropagation()}>
                      <div className="d-flex flex-nowrap justify-content-end gap-1">
                        <button
                          onClick={() => navigate(`/division/${div.id}`)}
                          className="btn btn-outline-primary btn-sm rounded-circle flex-shrink-0"
                          style={{ width: '32px', height: '32px' }}
                          title="Detail"
                        >
                          <i className="bi bi-eye-fill"></i>
                        </button>
                        <button
                          onClick={() => openEditModal(div)}
                          className="btn btn-outline-secondary btn-sm rounded-circle flex-shrink-0"
                          style={{ width: '32px', height: '32px' }}
                          title="Edit"
                        >
                          <i className="bi bi-pencil-fill"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(div.id, div.name)}
                          className="btn btn-outline-danger btn-sm rounded-circle flex-shrink-0"
                          style={{ width: '32px', height: '32px' }}
                          title="Hapus"
                        >
                          <i className="bi bi-trash3-fill"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    <div className="text-muted mb-3 opacity-25">
                      <i className="bi bi-inbox fs-1"></i>
                    </div>
                    <h6 className="fw-bold text-dark mb-1">Belum ada divisi</h6>
                    <p className="text-muted small mb-0">
                      Belum ada divisi yang terdaftar untuk saat ini.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </DataTableCard>

      {/* Modal Bootstrap */}
      {showModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content rounded-4 border-0 shadow">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold text-primary">
                    {isEditMode ? 'Edit Divisi' : 'Tambah Divisi Baru'}
                  </h5>
                  <button type="button" className="btn-close" onClick={handleCloseModal} aria-label="Close"></button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="modal-body py-3">
                    {/* Name */}
                    <div className="mb-3">
                      <label className="form-label small fw-semibold text-secondary">Nama Divisi</label>
                      <input
                        type="text"
                        className="form-control rounded-3"
                        placeholder="Contoh: Program & Partnership"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>

                    {/* Description */}
                    <div className="mb-3">
                      <label className="form-label small fw-semibold text-secondary">Deskripsi</label>
                      <textarea
                        className="form-control rounded-3"
                        placeholder="Tuliskan deskripsi divisi..."
                        rows="3"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </div>

                    {/* Manager Selection */}
                    <div className="mb-3">
                      <label className="form-label small fw-semibold text-secondary">Manajer (Penanggung Jawab)</label>
                      <select
                        className="form-select rounded-3"
                        value={employeeId}
                        onChange={(e) => setEmployeeId(e.target.value)}
                      >
                        <option value="">-- Belum ditentukan --</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.firstName} {emp.lastName || ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Status */}
                    <div className="mb-3">
                      <label className="form-label small fw-semibold text-secondary">Status Keaktifan</label>
                      <select
                        className="form-select rounded-3"
                        value={status}
                        onChange={(e) => setStatus(Number(e.target.value))}
                      >
                        <option value={1}>Aktif</option>
                        <option value={0}>Nonaktif</option>
                      </select>
                    </div>
                  </div>

                  <div className="modal-footer border-0 pt-0">
                    <button type="button" className="btn btn-outline-secondary rounded-pill px-3" onClick={handleCloseModal}>
                      Batal
                    </button>
                    <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={submitting}>
                      {submitting ? 'Menyimpan...' : 'Simpan'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
        </>
      )}
    </div>
  );
};

export default DivisionList;
